import { useCallback } from "react";
import { useCurrency } from "./useCurrency";
import {
  formatMoney,
  formatMoneyCompact,
  MoneyFormatOptions,
} from "@/shared/utils/money";

const LANGUAGE_TO_LOCALE: Record<string, string> = {
  en: "en-NP",
  ne: "ne-NP",
  hi: "hi-IN",
};

const FALLBACK_LOCALE = "en-NP";

export interface UseMoneyReturn {
  currency: string;
  language: string;
  locale: string;
  format: (amount: unknown, overrides?: MoneyFormatOptions) => string;
  formatCompact: (amount: unknown, overrides?: MoneyFormatOptions) => string;
}

export function useMoney(): UseMoneyReturn {
  const { currency, language } = useCurrency();
  const locale = LANGUAGE_TO_LOCALE[language] ?? FALLBACK_LOCALE;

  // An explicit `currency: undefined` override (e.g. a preview product or an
  // order record that carries no currency of its own) must NOT clobber the
  // active store currency and fall back to formatMoney's hardcoded NPR default.
  // Resolve the override against the store currency instead.
  const format = useCallback(
    (amount: unknown, overrides: MoneyFormatOptions = {}): string => {
      const { currency: overrideCurrency, ...rest } = overrides;
      return formatMoney(amount, {
        currency: overrideCurrency ?? currency,
        locale,
        ...rest,
      });
    },
    [currency, locale],
  );

  const formatCompact = useCallback(
    (amount: unknown, overrides: MoneyFormatOptions = {}): string => {
      const { currency: overrideCurrency, ...rest } = overrides;
      return formatMoneyCompact(amount, {
        currency: overrideCurrency ?? currency,
        locale,
        ...rest,
      });
    },
    [currency, locale],
  );

  return { currency, language, locale, format, formatCompact };
}
